<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { useTolgee, useTranslate } from '@tolgee/vue'
import { useSeoMeta } from '@unhead/vue'

import {
  BackgroundTheme,
  type IEditorSettings,
  type IGoalSettings,
  type IHeroContent,
  IMPACT_TYPES,
  type IProjectRequestParams,
  LANGUAGES,
  type LanguageType,
} from '@/api/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { usePublicData } from '@/stores/PublicData'

import PublicComparisonCardRow from '@/components/public-page/PublicComparisonCardRow.vue'
import PublicComparisonCardLoader from '@/components/public-page/PublicComparisonCardLoader.vue'
import PublicHero from '@/components/public-page/PublicHero.vue'
import PublicMap from '@/components/public-page/PublicMap.vue'
import PublicTopStatCardList from '@/components/public-page/PublicTopStatCardList.vue'
import PublicTopStatCardListLoader from '@/components/public-page/PublicTopStatCardListLoader.vue'
import PublicLedger from '@/components/public-page/PublicLedger.vue'
import PublicLedgerLoader from '@/components/public-page/PublicLedgerLoader.vue'
import PublicFooter from '@/components/public-page/PublicFooter.vue'
import PublicJoinGreenspark from '@/components/public-page/PublicJoinGreenspark.vue'
import PublicMapLoader from '@/components/public-page/PublicMapLoader.vue'
import PublicGoalsAchievements from '@/components/public-page/PublicGoalsAchievements.vue'

const publicDataStore = usePublicData()
const editorStore = useEditorStore()
const tolgee = useTolgee(['language'])
const { t } = useTranslate('local')
const locale: LanguageType = tolgee.value.getLanguage() || import.meta.env.VITE_DEFAULT_LOCALE
const projectParams: IProjectRequestParams = { statuses: ['approved', 'deprecated'] }
const pageTitle = `Greenspark - Greenspark Total Impact`
const pageDescription =
  'Embed environmental action into your daily life, grow your impact and join a global movement creating positive impact every day.'
const isInitialDataLoading = ref(true)
const canRenderMap = ref(false)
const isPurchasesLoading = ref(true)
const isTopStatsReady = ref(false)

const handleTopStatsReady = () => {
  isTopStatsReady.value = true
  window.requestAnimationFrame(() => {
    if (!isInitialDataLoading.value && isTopStatsReady.value) {
      canRenderMap.value = true
    }
  })
}

watch(isInitialDataLoading, async (isLoading) => {
  if (isLoading) {
    canRenderMap.value = false
    isTopStatsReady.value = false
    return
  }
})

const editorSettings: IEditorSettings = {
  background: {
    theme: BackgroundTheme.CUSTOM,
    icon: 'leaves',
    themeColor: {
      background: '#336E6B',
      text: '#FFFFFF',
    },
  },
  hero: {
    style: 'center',
    logo: {
      url: '',
      visible: true,
    },
    image: {
      url: 'https://res.cloudinary.com/dlilerh6s/image/upload/v1726233751/editor/poialivwzfzn8qqnlhzw.jpg',
      visible: true,
      overlayColor: '#336E6B',
      textColor: '#FFFFFF',
    },
    content: {
      ...LANGUAGES.reduce(
        (acc, lang) => {
          acc[lang] = {
            title: '',
            subtitle: '',
          }
          return acc
        },
        {} as Record<LanguageType, IHeroContent>,
      ),
      en: {
        title: t.value('CommunityPage.title'),
        subtitle: t.value('CommunityPage.description'),
      },
    },
    links: {
      facebook: '',
      instagram: '',
      linkedin: '',
      website: '',
      twitter: '',
      tiktok: '',
      youtube: '',
    },
    memberSince: {
      visible: true,
    },
  },
  icon: {
    style: 'isometric',
    impactColors: {
      carbon: {
        backgroundColor: '#FFB339',
        textColor: '#FFFFFF',
      },
      trees: {
        backgroundColor: '#49E2A7',
        textColor: '#FFFFFF',
      },
      plastic: {
        backgroundColor: '#1FAEF0',
        textColor: '#FFFFFF',
      },
      kelp: {
        backgroundColor: '#3D9B98',
        textColor: '#FFFFFF',
      },
      water: {
        backgroundColor: '#6074FF',
        textColor: '#FFFFFF',
      },
      bees: {
        backgroundColor: '#EACA78',
        textColor: '#FFFFFF',
      },
    },
    impactDisplay: {
      carbon: true,
      trees: true,
      plastic: true,
      kelp: true,
      water: true,
      bees: true,
      monthsEarthPositive: false,
      hideZeroValues: true,
    },
  },
  goalsAchievements: {
    ...LANGUAGES.reduce(
      (acc, lang) => {
        acc[lang] = {
          goals: [],
          achievements: [],
        }
        return acc
      },
      {} as Record<LanguageType, IGoalSettings>,
    ),
    en: {
      goals: [
        t.value('CommunityPage.goals.first'),
        t.value('CommunityPage.goals.second'),
        t.value('CommunityPage.goals.third'),
      ],
      achievements: [
        t.value('CommunityPage.achievements.first'),
        t.value('CommunityPage.achievements.second'),
        t.value('CommunityPage.achievements.third'),
      ],
    },
  },
}

const scrollToLedger = () => {
  if (window.location.hash === '#ledger') {
    const element = document.getElementById('ledger')

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }
}

const init = () => {
  publicDataStore.loading = true
  isInitialDataLoading.value = true
  isPurchasesLoading.value = true
  editorStore.$patch({ editor: { ...editorSettings } })

  if (window.navigator.language === 'en-US') tolgee.value.changeLanguage('en-US')
  Promise.all([
    publicDataStore.fetchCommunityData(),
    publicDataStore.fetchProjects({ ...projectParams }),
  ])
    .catch((error) => console.error(error))
    .finally(() => {
      isInitialDataLoading.value = false
      publicDataStore.loading = false
      setTimeout(() => scrollToLedger(), 100)
    })

  publicDataStore.fetchPurchases().finally(() => {
    isPurchasesLoading.value = false
  })
}

onMounted(() => {
  init()
})

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogType: 'website',
  ogDescription: pageDescription,
  ogUrl: window.location.href,
  ogImage:
    'https://cdn.prod.website-files.com/611391a1477389e3857d8014/673c84ac1ebcf01d18a6d07d_OG%20Image.png',
})
</script>

<template>
  <div
    :class="[
      'community-page',
      editorStore.editor.background.theme,
      editorStore.editor.background.icon,
    ]"
  >
    <PublicHero
      :editor-settings="editorStore.editor.hero"
      :theme="editorStore.editor.background.theme"
    />
    <PublicTopStatCardList
      v-if="!isInitialDataLoading && publicDataStore.getTopStats"
      :color-settings="editorStore.editor.icon.impactColors"
      :icon-style="editorStore.editor.icon.style"
      :public-stat-data="publicDataStore.getPublicStats"
      :top-stat-data="publicDataStore.getTopStats"
      @ready="handleTopStatsReady"
    />
    <PublicTopStatCardListLoader v-if="isInitialDataLoading" />
    <PublicMap
      v-if="publicDataStore.projectList.length && !isInitialDataLoading && canRenderMap"
      :color-settings="editorStore.editor.icon.impactColors"
      :projects="publicDataStore.getActiveAndDeprecatedProjects"
    />
    <PublicMapLoader
      v-if="
        isInitialDataLoading ||
        (!isInitialDataLoading && !canRenderMap && publicDataStore.projectList.length)
      "
    />
    <template v-for="impactType in IMPACT_TYPES" :key="impactType">
      <PublicComparisonCardRow
        v-if="
          publicDataStore.getTopStats[impactType] &&
          editorStore.editor.icon.impactDisplay[impactType] &&
          !isInitialDataLoading &&
          canRenderMap
        "
        :color-settings="editorStore.editor.icon.impactColors"
        :icon-style="editorStore.editor.icon.style"
        :impact-type="impactType"
        :top-stat-data="publicDataStore.getTopStats"
      />
      <PublicComparisonCardLoader v-if="isInitialDataLoading || !canRenderMap" />
    </template>
    <PublicGoalsAchievements
      v-if="
        editorStore.editor.goalsAchievements[locale]?.goals.length > 0 ||
        editorStore.editor.goalsAchievements[locale]?.achievements.length > 0
      "
    />
    <PublicJoinGreenspark />
    <PublicLedger
      v-if="!isPurchasesLoading"
      :purchases="publicDataStore.purchases"
      :transferred-receipts="publicDataStore.getTransferredImpactInvoices"
    />
    <PublicLedgerLoader v-else />
    <PublicFooter v-if="canRenderMap" />
  </div>
</template>

<style lang="scss" scoped>
/* Do not add overflow-x: hidden — it clips PublicMap carousel nav (negative inset). */
.community-page {
  background-color: var(--gray-light-f1);
  color: var(--ui-black);

  &.dark {
    background-color: var(--ui-dark);
    color: var(--lightest);
  }

  &.custom {
    background-color: var(--custom-theme-color);
    color: var(--lightest);
  }
}
</style>
